import { useIntl } from 'react-intl';
import { ResponsiveLine } from '@nivo/line';
import { Popover, Typography } from 'antd';
import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';
import { ContentWrapper, EllipsisButton, Group } from '@/components/_common';
import { useChartTheme, useConvertChartData, useDisclosure } from '@/hooks';
import moment from 'moment';
import { useEffect } from 'react';
import { useChartBillingPassAverageTicket } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import { DateTimeFormat } from '@/utils/moment';
import { BillingPassAverageTicketParams } from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';
import ChartDefaultFilter, {
  ChartDefaultFilterValues,
} from '../ChartDefaultFilter';
import { useColorScheme } from '@/contexts/ColorSchemeContextProvider';
import { Info } from 'react-feather';

const { Title, Paragraph } = Typography;

const defaultStartDate = moment().subtract(5, 'months').startOf('day');
const defaultEndDate = moment().endOf('day');

const SERIE_COLORS: Record<string, string> = {
  ['Faturamento']: '#FF6B6B',
  ['Ordem de Serviço']: '#4ECDC4',
  ['Ordem de serviço']: '#4ECDC4',
  ['Venda direta']: '#45B7D1',
  ['Passagem OS']: '#F9A826',
  ['Passagem VD']: '#A05195',
  ['Passagem']: '#808080',
  ['Ticket médio OS']: '#2E86AB',
  ['Ticket médio VD']: '#A23B72',
  ['Ticket médio real']: '#18A558',
  ['Serviços terceiros']: '#F24C4C',
};

function InvoicingBillingXTicketsXAverageTicket() {
  const intl = useIntl();
  const chartTheme = useChartTheme();
  const [modalVisible, modalVisibleAction] = useDisclosure();

  const [colorScheme] = useColorScheme();

  const chartBillingPassAverageTicket = useChartBillingPassAverageTicket();

  const chartData = useConvertChartData(
    chartBillingPassAverageTicket.data?.chartData,
  );

  const filteredChartData =
    chartData?.filter((line) => line.id !== 'Passagem') || [];

  useEffect(() => {
    chartBillingPassAverageTicket.mutateAsync({
      startDate: defaultStartDate.startOf('month').format(DateTimeFormat),
      endDate: defaultEndDate.endOf('month').format(DateTimeFormat),
    });
  }, []);

  const handleOk = async (values: ChartDefaultFilterValues) => {
    try {
      const obj: BillingPassAverageTicketParams = {
        ...values,
        startDate: values.startDate.format(DateTimeFormat),
        endDate: values.endDate.format(DateTimeFormat),
      };

      await chartBillingPassAverageTicket.mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  const items = [
    { label: 'Faturamento', desc: '(Total OS - Peças fornecidas) + Total VD' },
    { label: 'Ordem de serviço', desc: 'Total OS - Peças fornecidas' },
    { label: 'Venda direta', desc: 'Valor total de vendas diretas' },
    {
      label: 'Passagem OS',
      desc: 'Quantidade de OS fechadas',
    },
    {
      label: 'Passagem VD',
      desc: 'Quantidade de VD autorizadas',
    },
    {
      label: 'Passagem',
      desc: 'Quantidade de Passagem OS + Quantidade de Passagem de VD',
    },
    {
      label: 'Ticket médio OS',
      desc: '(Total OS - Peças fornecidas) / Passagem OS',
    },
    { label: 'Ticket médio VD', desc: 'Total VD / Passagem VD' },
    { label: 'Ticket médio real', desc: 'Faturamento / Passagem' },
    { label: 'Serviços terceiros', desc: 'Total serviços terceiros' },
  ];

  const StyledColorBox = (color: string) => (
    <div
      style={{
        width: '12px',
        height: '12px',
        backgroundColor: color,
        marginRight: '7px',
        borderRadius: '2px',
      }}
    />
  );

  const getSerieColor = (serieId: string | number): string => {
    const idString = String(serieId);
    return SERIE_COLORS[idString] || '#000000';
  };

  return (
    <>
      <ContentWrapper
        style={{ height: '430px', padding: '8px' }}
        extra={
          <Group>
            <Popover
              title="Faturamento x Passagens x Ticket Médio"
              placement="right"
              content={
                <div>
                  {items.map((item, index) => (
                    <p key={index}>
                      <strong>{item.label}:</strong> {item.desc}
                    </p>
                  ))}
                </div>
              }
            >
              <Info size={18} color="#364068" />
            </Popover>

            <EllipsisButton onClick={modalVisibleAction.open} />
          </Group>
        }
      >
        <Title level={5} style={{ textAlign: 'center', marginBottom: '4px' }}>
          Faturamento x Passagens x Ticket Médio
        </Title>
        <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
          {`Período: ${moment(
            chartBillingPassAverageTicket.variables?.startDate,
          ).format('DD/MM/YYYY')} a ${moment(
            chartBillingPassAverageTicket.variables?.endDate,
          ).format('DD/MM/YYYY')}`}
        </Paragraph>

        <ResponsiveLine
          data={filteredChartData}
          enableSlices="x"
          margin={{ top: 15, right: 25, bottom: 90, left: 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            reverse: false,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 7,
            tickPadding: 10,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 5,
            legend: intl.formatMessage({
              id: 'dashboard.averageTicketReport.averageTicketPartsAndServices',
            }),
            format: (value) => `${metricPrefixFormatter(+value)}`,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          colors={(serie) => getSerieColor(serie.id)}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh
          theme={chartTheme}
          yFormat={(value) => `${currencyFormatter(value)}`}
          sliceTooltip={({ slice }) => {
            const passagemLine = chartData?.find(
              (line) => line.id === 'Passagem',
            );
            const passagemValue = passagemLine?.data.find(
              (d) => d.x === slice.points[0].data.x,
            )?.y;

            return (
              <div
                style={{
                  background:
                    colorScheme === 'light' ? 'white' : 'rgb(68,68,68)',
                  padding: '5px 9px',
                  border: '2px',
                  borderRadius: 2,
                  boxShadow: 'rgba(0, 0, 0, 0.25) 0px 1px 2px',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {passagemValue !== undefined && (
                      <tr>
                        <td style={{ padding: '3px 5px' }}>
                          {StyledColorBox(getSerieColor('Passagem'))}
                        </td>
                        <td style={{ padding: '3px 5px' }}>Passagem</td>
                        <td style={{ padding: '3px 5px' }}>
                          <strong>{String(passagemValue)}</strong>
                        </td>
                      </tr>
                    )}

                    {slice.points.map((point) => {
                      const serieId = String(point.serieId);
                      const isPassagem = serieId.includes('Passagem');

                      return (
                        <tr key={point.id}>
                          <td style={{ padding: '3px 5px' }}>
                            {StyledColorBox(getSerieColor(serieId))}
                          </td>
                          <td style={{ padding: '3px 5px' }}>{serieId}</td>
                          <td style={{ padding: '3px 5px' }}>
                            <strong>
                              {isPassagem
                                ? String(point.data.y)
                                : currencyFormatter(point.data.y as number)}
                            </strong>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          }}
        />
      </ContentWrapper>

      <ChartDefaultFilter
        open={modalVisible}
        onOk={handleOk}
        onCancel={modalVisibleAction.close}
        title="Faturamento x Passagens x Ticket Médio"
      />
    </>
  );
}

export default InvoicingBillingXTicketsXAverageTicket;
